import type React from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import type {DashboardItem} from "./services/dashboardsApi";
import {notifyDashboardError, useDeleteDashboardMutation} from "./services/dashboardsMutations";

type Props = {
    /** The dashboard to delete; null keeps the dialog closed. */
    target: DashboardItem | null;
    /** The databag whose "Your dashboards" list drops the deleted dashboard. */
    databagId: number;
    onClose: () => void;
};

/** Asks before one of your dashboards is deleted, then deletes it and closes. */
export const DeleteDashboardDialog: React.FC<Props> = ({target, databagId, onClose}) => {
    const {mutate: deleteDashboard, isPending: isDeleting} = useDeleteDashboardMutation(databagId);

    const confirm = () => {
        if (!target || target.dashgenId === null) return;
        deleteDashboard({dashboardId: target.dashgenId}, {
            onSuccess: onClose,
            onError: () => notifyDashboardError("delete", target.id),
        });
    };

    return (
        <Dialog className="dsh-portal" open={target !== null} onClose={() => !isDeleting && onClose()} maxWidth="xs" fullWidth>
            <DialogTitle>Delete dashboard?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {target
                        ? `“${target.title}” will be removed from your dashboards. This can't be undone.`
                        : "This dashboard will be removed from your dashboards. This can't be undone."}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={isDeleting}>Cancel</Button>
                <Button
                    color="error"
                    variant="contained"
                    sx={{color: "#FFF"}}
                    onClick={confirm}
                    disabled={isDeleting}
                    loading={isDeleting}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
};
